import React from 'react';
import { useStyletron } from 'baseui';
import { StyledRoot, StyledBody, StyledContents } from 'baseui/card';
import { Button, KIND as ButtonKind, SHAPE, SIZE } from 'baseui/button';
import { HeadingSmall, LabelMedium } from 'baseui/typography';
import { Plus, Delete } from 'baseui/icon';
import { useNavigate, useParams, Outlet } from 'react-router-dom';
import useStore from '../store';

export function RequestList() {
  const [css] = useStyletron();
  const store = useStore();
  const navigate = useNavigate();
  const { requestId } = useParams();
  const [collapsedEndpoints, setCollapsedEndpoints] = React.useState(new Set());
  const requestsFetched = React.useRef(false);
  const requestsFetchFailed = React.useRef(false);

  // Memoize the fetch function
  const fetchRequests = React.useCallback(async () => {
    if (!store.requests?.length && !requestsFetched.current && !store.error && !requestsFetchFailed.current) {
      requestsFetched.current = true;
      try {
        await store.fetchRequests();
      } catch (error) {
        console.error('Failed to fetch requests:', error);
        requestsFetchFailed.current = true;
      }
    }
  }, [store]);

  // Only fetch requests on mount, endpoints are handled by App.js
  React.useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Reset failed flag if store is reset
  React.useEffect(() => {
    if (store.requests?.length) {
      requestsFetchFailed.current = false;
    }
  }, [store.requests]);

  const handleNewRequest = () => {
    navigate('/requests/new/body');
  };

  const handleDeleteRequest = async (request) => {
    if (window.confirm(`Are you sure you want to delete request "${request.name}"?`)) {
      try {
        await store.deleteRequest(request.id);
        if (requestId === request.id) {
          navigate('/requests');
        }
      } catch (error) {
        console.error('Failed to delete request:', error);
        alert('Failed to delete request. Please try again.');
      }
    }
  };

  // Group requests by endpoint
  const groupedRequests = React.useMemo(() => {
    const groups = {};
    store.requests.forEach(request => {
      const endpoint = store.endpoints.find(e => e.id === request.endpointId);
      if (endpoint) {
        const key = endpoint.id;
        if (!groups[key]) {
          groups[key] = {
            endpoint,
            requests: []
          };
        }
        groups[key].requests.push(request);
      }
    });
    return groups;
  }, [store.requests, store.endpoints]);

  const toggleEndpointCollapse = (endpointKey) => {
    setCollapsedEndpoints(prev => {
      const next = new Set(prev);
      if (next.has(endpointKey)) {
        next.delete(endpointKey);
      } else {
        next.add(endpointKey);
      }
      return next;
    });
  };

  return (
    <div className={css({
      display: 'flex',
      backgroundColor: '#fff',
      height: 'calc(100vh - 120px)',
      gap: '24px'
    })}>
      {/* Left panel - Request list */}
      <div className={css({
        width: '300px',
        borderRight: '1px solid #E2E2E2',
        padding: '24px',
        overflowY: 'auto'
      })}>
        <div className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        })}>
          <HeadingSmall margin="0">Requests</HeadingSmall>
          <Button
            onClick={handleNewRequest}
            startEnhancer={<Plus />}
            size={SIZE.compact}
          >
            New
          </Button>
        </div>

        {store.error && (
          <div
            className={css({
              backgroundColor: '#FFEBEE',
              color: '#C62828',
              padding: '16px 24px',
              marginBottom: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            })}
          >
            <LabelMedium>{store.error}</LabelMedium>
          </div>
        )}

        {store.loading ? (
          <div
            className={css({
              backgroundColor: '#fff',
              padding: '16px 24px',
              marginBottom: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              textAlign: 'center'
            })}
          >
            <LabelMedium>Loading requests...</LabelMedium>
          </div>
        ) : !store.error && Object.keys(groupedRequests).length === 0 ? (
          <div
            className={css({
              backgroundColor: '#fff',
              padding: '16px 24px',
              marginBottom: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              textAlign: 'center'
            })}
          >
            <LabelMedium>No requests found. Click "New" to create one.</LabelMedium>
          </div>
        ) : !store.error && Object.entries(groupedRequests).map(([endpointKey, group]) => (
          <div key={endpointKey}>
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px',
                backgroundColor: '#F6F6F6',
                marginBottom: '8px',
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: '#EEEEEE'
                }
              })}
              onClick={() => toggleEndpointCollapse(endpointKey)}
            >
              <div>
                <div className={css({
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333'
                })}>
                  {group.endpoint.name}
                </div>
                <div className={css({
                  fontSize: '12px',
                  color: '#666'
                })}>
                  {group.endpoint.method} {group.endpoint.path}
                </div>
              </div>
              <div className={css({
                transform: collapsedEndpoints.has(endpointKey) ? 'rotate(-90deg)' : 'rotate(0)',
                transition: 'transform 0.2s'
              })}>
                ▼
              </div>
            </div>
            {!collapsedEndpoints.has(endpointKey) && group.requests.map(request => (
              <StyledRoot
                key={request.id}
                onClick={() => navigate(`/requests/${request.id}/body`)}
                $style={{
                  marginBottom: '8px',
                  marginLeft: '8px',
                  cursor: 'pointer',
                  backgroundColor: requestId === request.id ? 
                    'rgb(246, 246, 246)' : '#fff',
                  ':hover': {
                    backgroundColor: 'rgb(246, 246, 246)'
                  }
                }}
              >
                <StyledContents>
                  <StyledBody>
                    <div className={css({
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px'
                    })}>
                      <div>
                        <div className={css({ fontWeight: 500 })}>{request.name}</div>
                        <div className={css({ 
                          color: 'rgb(128, 128, 128)',
                          fontSize: '12px'
                        })}>
                          {request.description || 'No description'}
                        </div>
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRequest(request);
                        }}
                        kind={ButtonKind.tertiary}
                        shape={SHAPE.round}
                        size={SIZE.mini}
                      >
                        <Delete size={16} />
                      </Button>
                    </div>
                  </StyledBody>
                </StyledContents>
              </StyledRoot>
            ))}
          </div>
        ))}
      </div>

      {/* Right panel - Request editor */}
      <Outlet />
    </div>
  );
}
